import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { Tooltip } from "../../../../bootstrap";
import { DayAgendaItemContext } from "./dayAgendaContext";
import { DayAgendaModel } from "./dayAgendaModel";
import { AgendaItemStyle } from "./dayAgendaOptions";
import { COLUMN_ITEM_BOTTOM_HANDLE_CLASS, COLUMN_ITEM_CLASS, COLUMN_ITEM_HANDLED_CLASS, COLUMN_ITEM_HANDLE_CLASS, COLUMN_ITEM_INNER_CLASS, COLUMN_ITEM_SELECTED_CLASS, COLUMN_ITEM_TOP_HANDLE_CLASS, DATA_ITEM_UID_ATTRIBUTE, ITEM_HANDLE_INTERACTION_CLASS, ITEM_HANDLE_INTERACTION_CURSOR } from "./dayAgendaUi.helper.consts";

/**
 * Default item render function.
 * @param agendaModel Agenda model
 * @param itemContext Item context
 * @param mainDiv Main item div
 * @param innerDiv Inner item div
 */
export function defaultItemRender<ItemObject, ColumnObject>(agendaModel: DayAgendaModel<ItemObject, ColumnObject>, itemContext: DayAgendaItemContext<ItemObject>, mainDiv: HTMLElement, innerDiv: HTMLElement): void {
    innerDiv.innerHTML = agendaModel.getItemText(itemContext.item);
}


/** 
 * Create div representing the data
 */
export function createItemDiv(itemContext: {
    itemUid: string,
    relativeTop: number,
    height: number,
    itemStyle: AgendaItemStyle,
    itemTooltip?: ValueOrFunction<AcceptedHtmlElementContent>,
    itemTooltipOptions?: Partial<Tooltip.Options>,
    zIndex?: number
}, options?: {
    tooltipContainer: HTMLElement,
    isEditable?: boolean,
    isResizable?: boolean
}): {
    div: HTMLElement,
    innerDiv: HTMLElement,
    leftHandleDiv?: HTMLElement,
    rightHandleDiv?: HTMLElement,
    itemTooltip?: Tooltip
} {
    const div = createItemDivContainer(itemContext);
    return fillItemDiv(div, itemContext, options);
}

/** 
 * Create div with no style and no content
 */
export function createItemDivContainer(itemContext: {
    itemUid: string,
    height: number,
    zIndex?: number
}): HTMLElement {
    // Create div representing the data
    const div = createHtmlElement("div", {
        cssClass: [COLUMN_ITEM_CLASS],
        style: {
            height: toPx(itemContext.height)
        },
        attributes: {
            [DATA_ITEM_UID_ATTRIBUTE]: itemContext.itemUid
        }
    });
    if (itemContext.zIndex !== undefined) {
        div.style.zIndex = String(itemContext.zIndex);
    }

    return div;
}

/** 
 * Place content and style of div representing the data
 */
export function fillItemDiv(div: HTMLElement, itemContext: {
    itemUid: string,
    height: number,
    itemStyle: AgendaItemStyle,
    itemTooltip?: ValueOrFunction<AcceptedHtmlElementContent>,
    itemTooltipOptions?: Partial<Tooltip.Options>,
    zIndex?: number
}, options?: {
    /** ! Can be override by itemContext.tooltipOptions ! */
    tooltipContainer: HTMLElement,
    isEditable?: boolean,
    isResizable?: boolean
}): {
    div: HTMLElement,
    innerDiv: HTMLElement,
    leftHandleDiv?: HTMLElement,
    rightHandleDiv?: HTMLElement
} {

    // Set background style
    if (itemContext.itemStyle.background != null) {
        div.style.background = itemContext.itemStyle.background;
    }

    // Set border style
    if (itemContext.itemStyle.borderWidth != null) {
        div.style.border = itemContext.itemStyle.borderWidth + "px " + (itemContext.itemStyle.borderStyle ?? "solid") + " " + (itemContext.itemStyle.borderColor ?? "");
    }

    // Set border radius
    if (itemContext.itemStyle.borderRadius != null && itemContext.itemStyle.borderRadius > 0) {
        div.style.borderRadius = itemContext.itemStyle.borderRadius + "px";
    }

    if (itemContext.itemTooltip && options?.tooltipContainer) {
        div.setAttribute("data-bs-toggle", "tooltip");
        div.setAttribute("data-bs-html", "true");

        // Register tooltip only on mouseEnter
        div.addEventListener("mouseenter", () => {
            // Register tooltip
            const tooltipContent = getVOFValue(itemContext.itemTooltip);
            if (tooltipContent) {
                const tooltip = new Tooltip(div, {
                    // Apply custom options first, title and container cannot be override
                    ...itemContext.itemTooltipOptions,
                    title: tooltipContent,
                    container: itemContext.itemTooltipOptions?.container ?? options.tooltipContainer,
                    html: true
                });
                // Show tooltip
                setTimeout(() => tooltip.show());
            }
        }, { once: true });

    }

    // Create inner div for moving purpose (also contains the text)
    const innerDiv = createHtmlElement("div", { cssClass: [COLUMN_ITEM_INNER_CLASS] });

    if (itemContext.itemStyle.textColor != null) {
        innerDiv.style.color = itemContext.itemStyle.textColor;
    }

    const result = {
        div,
        innerDiv,
        topHandleDiv: undefined as HTMLElement | undefined,
        bottomHandleDiv: undefined as HTMLElement | undefined
    };

    if (options?.isEditable && options?.isResizable) {
        // Add handled class to item, to apply correct style
        innerDiv.classList.add(COLUMN_ITEM_HANDLED_CLASS);

        result.topHandleDiv = createHtmlElement("div", { cssClass: options?.isEditable ? [COLUMN_ITEM_TOP_HANDLE_CLASS, COLUMN_ITEM_HANDLE_CLASS, ITEM_HANDLE_INTERACTION_CLASS] : [COLUMN_ITEM_TOP_HANDLE_CLASS, COLUMN_ITEM_HANDLE_CLASS] });
        result.bottomHandleDiv = createHtmlElement("div", { cssClass: options?.isEditable ? [COLUMN_ITEM_BOTTOM_HANDLE_CLASS, COLUMN_ITEM_HANDLE_CLASS, ITEM_HANDLE_INTERACTION_CLASS] : [COLUMN_ITEM_BOTTOM_HANDLE_CLASS, COLUMN_ITEM_HANDLE_CLASS] });

        const styleHoverHandle = createHtmlElement("style");
        // We set the handle's hover color here to use the custom item's style
        const cssHoverHandle = `
                [${DATA_ITEM_UID_ATTRIBUTE}="${itemContext.itemUid}"] > .${ITEM_HANDLE_INTERACTION_CLASS}:hover {
                        cursor: ${ITEM_HANDLE_INTERACTION_CURSOR};
                        background-color:${itemContext.itemStyle.handleHoverColor}
                }
                [${DATA_ITEM_UID_ATTRIBUTE}="${itemContext.itemUid}"].${COLUMN_ITEM_SELECTED_CLASS} > .${ITEM_HANDLE_INTERACTION_CLASS}:hover {
                    background-color: ${itemContext.itemStyle.selectedHandleColor};
                }
            `;
        styleHoverHandle.append(document.createTextNode(cssHoverHandle));
        div.style.cursor = "grab";
        div.append(styleHoverHandle, result.topHandleDiv, innerDiv, result.bottomHandleDiv);
    } else {
        div.append(innerDiv);
    }

    return result;
}
